//import modules from design system
import { configSearch, configSite } from "@pub/config";
import { Dropdown, Loader, Typography } from "@sphtech/dmg-design-system";
import SearchForm from "@src/app/components/Modules/SearchForm/SearchForm";
import SearchResults from "@src/app/layouts/Search/SearchResults";
import { TeaserDataApiResponse } from "@src/app/types/ServerCustomContext";
import { FetchError } from "@src/app/utils/swr";
import { LOAD_MORE_TEXT } from "@src/pubs/common/config";
//import classnames
import cx from "classnames";
//import react
import { Fragment, ReactNode } from "react";

import styles from "./search.module.css";

const { Heading } = Typography;

type Props = {
  isLoading?: boolean;
  searchTerm?: string;
  searchSetting: string;
  updateSearchSetting: React.ChangeEventHandler<HTMLSelectElement>;
  error?: FetchError;
  data?: TeaserDataApiResponse[];
  loadMore?: () => void;
  isLoadingMore: boolean;
  onSearch: React.ComponentProps<typeof SearchForm>["onSubmit"];
};

function SearchLayout({
  data,
  onSearch,
  searchTerm,
  searchSetting,
  updateSearchSetting,
  loadMore,
  isLoading,
  isLoadingMore,
  error,
  //response,
}: Props): ReactNode {
  const latestApiData = data?.at(-1);
  const firstApiData = data?.[0];

  const searchSettingOptions = configSearch.sortByOptions || [
    { label: "Relevance", value: "score" },
    { label: "Latest", value: "latest" },
  ];

  return (
    <Fragment>
      {/* {SearchBox} */}
      <section
        className={cx(
          styles.bodyContainer,
          styles.searchBoxContainer,
          searchTerm === "" && styles.searchBoxInitialMarginBottom,
        )}
      >
        <SearchForm value={searchTerm} onSubmit={onSearch} />
      </section>

      {/* {Result Heading } */}
      {latestApiData?.total !== undefined && (
        <section className={styles.bodyContainer}>
          <div className={styles.searchHeader}>
            <Heading.SubHeading3>
              {configSearch.withResultsText
                ? `${latestApiData.total} ${configSearch.withResultsText}`
                : `Found ${
                    latestApiData.total === 1
                      ? "one result"
                      : `${latestApiData.total} results`
                  }`}
            </Heading.SubHeading3>
            <Dropdown
              label={configSearch.sortByText || "Sort by:"}
              name="search"
              id="search"
              value={searchSetting}
              onChange={updateSearchSetting}
              options={searchSettingOptions}
            />
          </div>
        </section>
      )}

      {firstApiData?.results?.length === 0 && (
        <>
          <div
            className={cx(
              styles.bodyContainer,
              styles.gutterBottom,
              styles.noResults,
            )}
          >
            <Typography.Body>
              {configSearch.noResultsText
                ? `${configSearch.noResultsText.prefix} "${searchTerm}" ${configSearch.noResultsText.suffix}`
                : `Sorry, we couldn't find anything for “${searchTerm}”.`}
            </Typography.Body>
          </div>
        </>
      )}

      {isLoading && (
        <div className={cx(styles.bodyContainer, styles.gutterBottom)}>
          <Loader
            label={
              configSite.loadMore?.text ||
              configSearch.loadMoreText ||
              LOAD_MORE_TEXT
            }
            variant={configSite.loadMore?.variant}
          />
        </div>
      )}

      {!!error && (
        <div className={cx(styles.bodyContainer, styles.gutterBottom)}>
          <Typography.Body>
            {error.statusCode === 400
              ? "Your search request cannot be processed."
              : "An error occured! Please try again."}
          </Typography.Body>
        </div>
      )}

      {/* {Search Results Infinite scroll} */}
      {data?.map((data, index, all) => {
        const isLast = all.length - 1 === index;
        if (!data?.results?.length) {
          return;
        }
        return (
          <SearchResults
            key={index}
            data={data.results}
            showLeaderboard={index > 0}
            loadMore={isLast ? loadMore : undefined}
            isLoadingMore={isLast && isLoadingMore}
          />
        );
      })}
    </Fragment>
  );
}

export default SearchLayout;
